/**
 * Balanço contábil gerencial.
 * Caixa, estoque e títulos vêm do ERP. Demais itens são cadastrados na página.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Calendar, ChevronDown, ChevronRight, Download, Pencil, Plus, RotateCcw, Scale, Landmark, ArrowDownRight } from 'lucide-react'
import { balanceSheetService } from '../../../../services/balanceSheetService'
import { exportElementToPdf } from '../../../../utils/exportElementToPdf'
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import BalancoPdfCanvas from './BalancoPdfCanvas'
import './BalancoPage.css'

const GROUP_OPTIONS = [
  { value: 'asset_non_current', label: 'Ativo não circulante' },
  { value: 'liability', label: 'Passivo' },
  { value: 'equity', label: 'Patrimônio líquido' }
]

const EMPTY_FORM = {
  name: '',
  group: 'asset_non_current',
  amount: '',
  notes: ''
}

function formatMoney(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0)
}

function formatAsOf(isoDate) {
  if (!isoDate) return ''
  const [year, month, day] = String(isoDate).slice(0, 10).split('-')
  if (!year || !month || !day) return isoDate
  return `${day}/${month}/${year}`
}

function ManualRows({ items, onEdit, onDeactivate }) {
  if (!items.length) {
    return <p className="balanco-empty">Nenhum item cadastrado neste grupo.</p>
  }

  return items.map((item) => (
    <div className="balanco-row" key={item.id}>
      <div className="balanco-row-label">
        <span>{item.name}</span>
        {item.notes ? <small>{item.notes}</small> : null}
      </div>
      <div className="balanco-row-actions">
        <strong>{formatMoney(item.amount)}</strong>
        <button type="button" onClick={() => onEdit(item)} aria-label={`Editar ${item.name}`}>
          <Pencil size={14} />
        </button>
        <button type="button" onClick={() => onDeactivate(item)} aria-label={`Inativar ${item.name}`}>
          Inativar
        </button>
      </div>
    </div>
  ))
}

function BalancoPage() {
  const { hasPermission } = usePermissions()
  const canView = hasPermission(MODULES.FINANCIAL, ACTIONS.FINANCIAL_BALANCE_SHEET)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const hasDataRef = useRef(false)
  const [sheet, setSheet] = useState(null)
  const [cashOpen, setCashOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const pdfRef = useRef(null)

  const load = useCallback(async () => {
    if (!canView) {
      setSheet(null)
      setLoading(false)
      setError('Você não tem permissão para visualizar o balanço contábil.')
      return
    }

    if (hasDataRef.current) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)
    try {
      const response = await balanceSheetService.get()
      setSheet(response.data)
      hasDataRef.current = true
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível carregar o balanço.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [canView])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = (group) => {
    setEditing(null)
    setForm({ ...EMPTY_FORM, group })
    setFormError('')
    setFormOpen(true)
  }

  const openEdit = (item) => {
    setEditing(item)
    setForm({
      name: item.name,
      group: item.group,
      amount: String(item.amount),
      notes: item.notes || ''
    })
    setFormError('')
    setFormOpen(true)
  }

  const handleDeactivate = async (item) => {
    if (!window.confirm(`Inativar "${item.name}"? O valor sai do balanço.`)) return
    try {
      await balanceSheetService.deactivateItem(item.id)
      await load()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível inativar o item.')
    }
  }

  const handleDownload = async () => {
    if (!pdfRef.current || !sheet) return
    setExporting(true)
    setError(null)
    try {
      await exportElementToPdf(pdfRef.current, `Balanco_Contabil_${sheet.asOf}.pdf`, {
        rowSelector: '.balanco-pdf-row',
        summarySelector: '.balanco-pdf-summary',
        footerSelector: '.balanco-pdf-footer'
      })
    } catch (err) {
      setError(err.message || 'Não foi possível gerar o PDF.')
    } finally {
      setExporting(false)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const amount = Number(String(form.amount).replace(',', '.'))
    if (!form.name.trim()) {
      setFormError('Informe o nome.')
      return
    }
    if (!Number.isFinite(amount) || amount < 0) {
      setFormError('Informe um valor zero ou maior.')
      return
    }

    setSaving(true)
    setFormError('')
    const payload = {
      name: form.name.trim(),
      group: form.group,
      amount,
      notes: form.notes.trim()
    }

    try {
      if (editing) {
        await balanceSheetService.updateItem(editing.id, payload)
      } else {
        await balanceSheetService.createItem(payload)
      }
      setFormOpen(false)
      await load()
    } catch (err) {
      setFormError(err.response?.data?.message || 'Não foi possível salvar o item.')
    } finally {
      setSaving(false)
    }
  }

  if (loading && !sheet) {
    return (
      <div className="balanco-state">
        <p>Carregando balanço...</p>
      </div>
    )
  }

  if (error && !sheet) {
    return (
      <div className="balanco-state balanco-state-error">
        <p>{error}</p>
        <button type="button" onClick={load}>Tentar novamente</button>
      </div>
    )
  }

  const assets = sheet.assets
  const liabilities = sheet.liabilities
  const equity = sheet.equity
  const difference = Number(sheet.equationDifference) || 0

  return (
    <div className={`balanco-page${refreshing ? ' is-refreshing' : ''}`}>
      <header className="balanco-header">
        <div>
          <h1 className="balanco-title">
            Balanço <span className="balanco-title-highlight">Contábil</span>
            {refreshing && (
              <RotateCcw size={20} className="balanco-refresh-icon" aria-hidden />
            )}
          </h1>
          <p className="balanco-subtitle">Posição em {formatAsOf(sheet.asOf)} · saldo de hoje, sem período de 12 meses ou de ano</p>
        </div>
        <div className="balanco-header-actions">
          <button
            type="button"
            className="balanco-reload"
            onClick={load}
            disabled={refreshing}
            aria-label="Atualizar balanço"
            title="Atualizar balanço"
          >
            <RotateCcw size={18} className={refreshing ? 'is-spinning' : undefined} />
          </button>
          <div className="balanco-period">
            <Calendar size={14} />
            <span>{formatAsOf(sheet.asOf)}</span>
          </div>
          <button
            type="button"
            className="balanco-pdf-btn"
            onClick={handleDownload}
            disabled={exporting || refreshing}
          >
            <Download size={16} />
            {exporting ? 'Gerando...' : 'PDF'}
          </button>
          <button type="button" className="balanco-new" onClick={() => openCreate('asset_non_current')}>
            <Plus size={16} />
            Adicionar item
          </button>
        </div>
      </header>

      {error ? (
        <div className="balanco-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={load}>Tentar novamente</button>
        </div>
      ) : null}

      <section className="balanco-kpi-grid">
        <article className="balanco-kpi">
          <div className="balanco-kpi-icon balanco-kpi-icon--asset">
            <Scale size={24} />
          </div>
          <p className="balanco-kpi-label">Ativo</p>
          <p className="balanco-kpi-value">{formatMoney(assets.total)}</p>
        </article>
        <article className="balanco-kpi">
          <div className="balanco-kpi-icon balanco-kpi-icon--liability">
            <ArrowDownRight size={24} />
          </div>
          <p className="balanco-kpi-label">Passivo</p>
          <p className="balanco-kpi-value">{formatMoney(liabilities.total)}</p>
        </article>
        <article className="balanco-kpi">
          <div className="balanco-kpi-icon balanco-kpi-icon--equity">
            <Landmark size={24} />
          </div>
          <p className="balanco-kpi-label">Patrimônio líquido</p>
          <p className="balanco-kpi-value">{formatMoney(equity.total)}</p>
        </article>
      </section>

      <div className="balanco-body">
      {Math.abs(difference) >= 0.01 ? (
        <p className="balanco-diff">Diferença de arredondamento: {formatMoney(difference)}</p>
      ) : null}

      <section className="balanco-group">
        <header>
          <h2>Ativo circulante</h2>
          <strong>{formatMoney(assets.current.total)}</strong>
        </header>

        <div className="balanco-row">
          <button type="button" className="balanco-expand" onClick={() => setCashOpen((open) => !open)}>
            {cashOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>Caixa e equivalentes</span>
            <em>calculado</em>
          </button>
          <strong>{formatMoney(assets.current.cash.total)}</strong>
        </div>
        {cashOpen ? (
          <div className="balanco-children">
            {assets.current.cash.accounts.length === 0 ? (
              <p className="balanco-empty">Nenhuma conta financeira ativa.</p>
            ) : assets.current.cash.accounts.map((account) => (
              <div className="balanco-row balanco-row-child" key={account.id}>
                <span>{account.name}</span>
                <strong>{formatMoney(account.balance)}</strong>
              </div>
            ))}
          </div>
        ) : null}

        <div className="balanco-row">
          <div className="balanco-row-label">
            <span>Contas a receber</span>
            <em>calculado</em>
          </div>
          <strong>{formatMoney(assets.current.receivables.total)}</strong>
        </div>
        <div className="balanco-row">
          <div className="balanco-row-label">
            <span>Estoques</span>
            <em>calculado</em>
          </div>
          <strong>{formatMoney(assets.current.inventory.total)}</strong>
        </div>
      </section>

      <section className="balanco-group">
        <header>
          <h2>Ativo não circulante</h2>
          <div className="balanco-header-side">
            <button type="button" onClick={() => openCreate('asset_non_current')}>Adicionar</button>
            <strong>{formatMoney(assets.nonCurrent.total)}</strong>
          </div>
        </header>
        <ManualRows items={assets.nonCurrent.items} onEdit={openEdit} onDeactivate={handleDeactivate} />
      </section>

      <section className="balanco-group">
        <header>
          <h2>Passivo</h2>
          <div className="balanco-header-side">
            <button type="button" onClick={() => openCreate('liability')}>Adicionar</button>
            <strong>{formatMoney(liabilities.total)}</strong>
          </div>
        </header>
        <div className="balanco-row">
          <div className="balanco-row-label">
            <span>Contas a pagar</span>
            <em>calculado</em>
          </div>
          <strong>{formatMoney(liabilities.payables.total)}</strong>
        </div>
        <ManualRows items={liabilities.items} onEdit={openEdit} onDeactivate={handleDeactivate} />
      </section>

      <section className="balanco-group">
        <header>
          <h2>Patrimônio líquido</h2>
          <div className="balanco-header-side">
            <button type="button" onClick={() => openCreate('equity')}>Adicionar</button>
            <strong>{formatMoney(equity.total)}</strong>
          </div>
        </header>
        <ManualRows items={equity.items} onEdit={openEdit} onDeactivate={handleDeactivate} />
        <div className="balanco-row">
          <div className="balanco-row-label">
            <span>Lucros ou prejuízos acumulados</span>
            <em>calculado</em>
          </div>
          <strong>{formatMoney(equity.retainedEarnings)}</strong>
        </div>
      </section>
      </div>

      <BalancoPdfCanvas ref={pdfRef} sheet={sheet} />

      {formOpen ? (
        <div className="balanco-modal-backdrop" onClick={() => !saving && setFormOpen(false)}>
          <form className="balanco-modal" onClick={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
            <h3>{editing ? 'Editar item' : 'Novo item'}</h3>
            <label>
              Nome
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                required
              />
            </label>
            <label>
              Grupo
              <select
                value={form.group}
                onChange={(event) => setForm((current) => ({ ...current, group: event.target.value }))}
              >
                {GROUP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Valor
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
                required
              />
            </label>
            <label>
              Observação
              <textarea
                rows={3}
                value={form.notes}
                onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              />
            </label>
            {formError ? <p className="balanco-form-error">{formError}</p> : null}
            <div className="balanco-modal-actions">
              <button type="button" onClick={() => setFormOpen(false)} disabled={saving}>Cancelar</button>
              <button type="submit" className="balanco-new" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}

export default BalancoPage
